import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useEffect, useState } from "react";
import {
  getExportChartTitle,
  isExportFormat,
  normalizeSelectedCharts,
  summarizeThroughputLineNames,
} from "@/features/deployment-dashboard/analytics/export/analytics-export.utils";
import {
  ALL_CHART_IDS,
  EXPORT_CHART_OPTIONS,
  EXPORT_FORMAT_OPTIONS,
  type ExportChartId,
  type ExportFormat,
} from "@/features/deployment-dashboard/analytics/export/types";
import { getAnalyticsAreaLabel } from "@/features/deployment-dashboard/shared/analytics-filters";
import type { ThroughputLineOption } from "@/features/deployment-dashboard/widgets/charts/throughput-trend";
import type { AnalyticsFilters, AreaLocation } from "@/features/deployment-dashboard/types";

function formatDateShort(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface SummaryRowProps {
  label: string;
  value: string;
}

function SummaryRow({ label, value }: SummaryRowProps) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "88px 1fr", gap: 1, alignItems: "baseline" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
      <Typography variant="caption" color="text.primary">
        {value}
      </Typography>
    </Box>
  );
}

export interface AnalyticsExportDialogProps {
  open: boolean;
  onClose: () => void;
  filters: AnalyticsFilters;
  areas: AreaLocation[];
  selectedThroughputLines: ThroughputLineOption[];
  isExporting: boolean;
  errorMessage: string | null;
  onExport: (format: ExportFormat, selectedCharts: readonly ExportChartId[]) => Promise<void>;
  onClearError: () => void;
}

/**
 * Export dialog for the Analytics page. Lets the user pick a format (PDF, PNG, CSV),
 * choose which charts to include via a multi-select, and previews a summary of the
 * active filters (date range, area, throughput line if applicable) alongside the
 * selected chart list before confirming the export.
 */
export function AnalyticsExportDialog({
  open,
  onClose,
  filters,
  areas,
  selectedThroughputLines,
  isExporting,
  errorMessage,
  onExport,
  onClearError,
}: AnalyticsExportDialogProps) {
  const [exportFormat, setExportFormat] = useState<ExportFormat>("pdf");
  const [selectedCharts, setSelectedCharts] = useState<ExportChartId[]>(() => [...ALL_CHART_IDS]);

  function handleFormatChange(value: string) {
    if (isExportFormat(value)) {
      setExportFormat(value);
      onClearError();
    }
  }

  function handleChartChange(event: SelectChangeEvent<string[]>) {
    const value = event.target.value;
    setSelectedCharts(
      normalizeSelectedCharts(typeof value === "string" ? value.split(",") : value)
    );
    onClearError();
  }

  useEffect(() => {
    if (open) onClearError();
  }, [open, onClearError]);

  async function handleExport() {
    await onExport(exportFormat, selectedCharts);
  }

  const chartSummaryLabel =
    selectedCharts.length === 0
      ? "None selected"
      : selectedCharts.map(getExportChartTitle).join(", ");

  return (
    <Dialog
      open={open}
      onClose={(_, _reason) => {
        if (!isExporting) onClose();
      }}

      aria-busy={isExporting}
      aria-labelledby="analytics-export-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="analytics-export-dialog-title">Export analytics</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <DialogContentText>
          Choose an export format and select which charts to include in the export.
        </DialogContentText>

        <TextField
          select
          fullWidth
          label="Export format"
          value={exportFormat}
          onChange={(event) => handleFormatChange(event.target.value)}
          disabled={isExporting}
        >
          {EXPORT_FORMAT_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <FormControl fullWidth>
          <InputLabel id="export-charts-label">Charts to export</InputLabel>
          <Select
            labelId="export-charts-label"
            multiple
            value={selectedCharts}
            onChange={handleChartChange}
            disabled={isExporting}
            input={<OutlinedInput label="Charts to export" />}
            renderValue={(selected) =>
              normalizeSelectedCharts(selected).map(getExportChartTitle).join(", ")
            }
          >
            {EXPORT_CHART_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={selectedCharts.includes(opt.value)} />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider />

        <Box
          sx={(theme) => ({
            p: 2,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: alpha(theme.palette.common.white, 0.04),
            border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
          })}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{
              display: "block",
              fontSize: "0.625rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              mb: 1.5,
            }}
          >
            Export Summary
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <SummaryRow
              label="Date range"
              value={`${formatDateShort(filters.startDate)} – ${formatDateShort(filters.endDate)}`}
            />
            <SummaryRow label="Area" value={getAnalyticsAreaLabel(filters.areaId, areas)} />
            {selectedCharts.includes("throughput-trend") && (
              <>
                <SummaryRow label="Lines" value={String(selectedThroughputLines.length)} />
                {selectedThroughputLines.length > 0 && (
                  <SummaryRow
                    label="Line names"
                    value={summarizeThroughputLineNames(
                      selectedThroughputLines.map((line) => ({
                        id: line.line_id,
                        name: line.line_name,
                        areaId: line.areaId,
                        areaName: line.areaName,
                        positiveDirectionLabel: line.positive_direction.label,
                        negativeDirectionLabel: line.negative_direction.label,
                      }))
                    )}
                  />
                )}
              </>
            )}
            <SummaryRow label="Charts" value={chartSummaryLabel} />
          </Box>
        </Box>
        {errorMessage != null && (
          <Alert severity="error" role="alert">
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={selectedCharts.length === 0 || isExporting}
          onClick={() => void handleExport()}
          startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isExporting ? "Preparing export…" : `Export ${exportFormat.toUpperCase()}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
